import type { ProspectResponse } from '@common/types/prospects';
import type { CreateVerificationRequest } from '@common/types/treefort';
import { getIsIntakeFormV2 } from '@common/utils/intakeFormVersion';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    Modal,
    type SxProps,
    TextField,
    type Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import CustomPhoneInput from '@/components/CustomPhoneInput';
import {
    useProspectIntakeForm,
    useVerificationRequests,
    useVerificationStatuses,
    type VerificationStatusDetail,
} from '@/queries/prospectDetails';
import { createIDVerificationEmail } from '@/services/TreefortService';
import { theme } from '@/theme';
import { extractContactDetails } from '@/utils';
import { getPhoneValidationError } from '@/utils/phone';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import {
    type IDVerificationClient,
    isIdVerificationClientDisabled,
    mapContactResponsesToIDVClients,
} from './idVerificationContacts';

interface Props {
    prospectId?: string;
    onClose: () => void;
    prospect: ProspectResponse;
}

export default function SendIDVerificationModal({ prospectId, onClose, prospect }: Props) {
    const { data: intakeForm } = useProspectIntakeForm(prospectId);
    const { data: verificationRequestResponseData = [] } = useVerificationRequests(prospectId);
    const intakeFormData = intakeForm?.data;
    const isIntakeFormV2 = getIsIntakeFormV2(prospect.practiceArea, prospect.matterType);
    const { data: latestStatuses = {} } = useVerificationStatuses(
        prospectId,
        verificationRequestResponseData,
        true,
    );

    const legacyExtractedDetails = useMemo<IDVerificationClient[]>(() => {
        if (!intakeFormData?.formResponse) return [];

        try {
            const parsed = JSON.parse(intakeFormData.formResponse);
            return extractContactDetails(parsed).map((contact) => ({
                ...contact,
                id: contact.id.toString(),
            }));
        } catch (error) {
            console.error('Error parsing formResponse:', error);
            return [];
        }
    }, [intakeFormData]);

    const extractedDetails = useMemo(
        () =>
            isIntakeFormV2
                ? mapContactResponsesToIDVClients(intakeFormData?.contactResponses)
                : legacyExtractedDetails,
        [intakeFormData?.contactResponses, isIntakeFormV2, legacyExtractedDetails],
    );

    const contactDetails = useMemo(() => {
        return {
            details: intakeFormData?.userContactDetails ?? [],
            prospectVersionId: intakeFormData?.prospectVersion?.prospectVersionId ?? null,
        };
    }, [intakeFormData]);

    const emailToContactIdMap = useMemo(
        () =>
            contactDetails.details.reduce(
                (map, detail) => {
                    if (detail.email) {
                        map[detail.email] = detail.contactDetailId;
                    }
                    return map;
                },
                {} as Record<string, string>,
            ),
        [contactDetails.details],
    );

    const clientsForVerification = useMemo(
        () =>
            extractedDetails.map((item) => ({
                ...item,
                contactDetailId: item.contactDetailId ?? emailToContactIdMap[item.email],
            })),
        [emailToContactIdMap, extractedDetails],
    );

    return (
        <Modal open onClose={onClose}>
            <SendIDVerificationModalForm
                key={prospectId}
                prospectId={prospectId}
                prospect={prospect}
                onClose={onClose}
                clientsForVerification={clientsForVerification}
                contactDetails={contactDetails}
                latestStatuses={latestStatuses}
            />
        </Modal>
    );
}

interface SendIDVerificationModalFormProps {
    prospectId?: string;
    prospect: ProspectResponse;
    onClose: () => void;
    clientsForVerification: IDVerificationClient[];
    contactDetails: {
        details: { contactDetailId: string; email?: string }[];
        prospectVersionId: number | null;
    };
    latestStatuses: Record<string, VerificationStatusDetail>;
}

function SendIDVerificationModalForm({
    prospectId,
    prospect,
    onClose,
    clientsForVerification,
    contactDetails,
    latestStatuses,
}: SendIDVerificationModalFormProps) {
    const queryClient = useQueryClient();
    const [selectedClients, setSelectedClients] = useState(() =>
        clientsForVerification
            .filter(
                (client) => !isIdVerificationClientDisabled(client.contactDetailId, latestStatuses),
            )
            .map((client) => client.id),
    );
    const [idVerification, setIdVerification] = useState<Record<string, boolean>>({});
    const [isLoadingButton, setIsLoadingButton] = useState(false);
    const [bankReview, setBankReview] = useState<Record<string, boolean>>({});
    const [clients, setClients] = useState(clientsForVerification);
    const [errors, setErrors] = useState<Record<string, { email: boolean; phone: boolean }>>({});

    const effectiveSelectedClients = useMemo(
        () =>
            selectedClients.filter((clientId) => {
                const client = clients.find((entry) => entry.id === clientId);
                return (
                    client &&
                    !isIdVerificationClientDisabled(client.contactDetailId, latestStatuses)
                );
            }),
        [clients, latestStatuses, selectedClients],
    );

    const modalStyles: SxProps<Theme> = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '800px',
        height: '90vh',
        width: '100%',
        bgcolor: theme.palette.neutral.white,
        borderRadius: '8px',
    };

    const modalBodyStyles: SxProps<Theme> = {
        padding: '24px 24px 16px 24px',
        flex: 1,
        overflowY: 'auto',
        maxHeight: '75vh',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    };

    const handlePhoneChange = (clientId: string, phone: string) => {
        setClients((prevClients) =>
            prevClients.map((client) => (client.id === clientId ? { ...client, phone } : client)),
        );

        setErrors((prevErrors) => ({
            ...prevErrors,
            [clientId]: { ...prevErrors[clientId], phone: undefined },
        }));
    };

    const handleCountryChange = (
        clientId: string,
        countryData: { code: string; dialCode: string },
    ) => {
        setClients((prevClients) =>
            prevClients.map((client) =>
                client.id === clientId
                    ? {
                          ...client,
                          countryCode: countryData.code,
                          dialCode: Number(countryData.dialCode),
                      }
                    : client,
            ),
        );

        setErrors((prevErrors) => ({
            ...prevErrors,
            [clientId]: { ...prevErrors[clientId], phone: undefined },
        }));
    };

    const handleSendIDVerification = async (payload: CreateVerificationRequest) => {
        if (prospectId) {
            setIsLoadingButton(true);
            try {
                await createIDVerificationEmail(payload);
                successSnackbar('ID Verification Initiated successfully.');
                onClose();
                setIsLoadingButton(false);
            } catch (error) {
                console.error(error);
                errorSnackbar('Failed to send ID Verification. Please try again.');
                setIsLoadingButton(false);
            }
        }
    };

    const handleClientChange = (id: string, field: string, value: string) => {
        setClients((prevClients) =>
            prevClients.map((client) =>
                client.id === id ? { ...client, [field]: value } : client,
            ),
        );

        // Clear the error if the user starts typing
        if (value.trim()) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [id]: {
                    email: field === 'email' ? false : prevErrors[id]?.email,
                    phone: field === 'phone' ? false : prevErrors[id]?.phone,
                },
            }));
        }
    };

    const selectableClients = useMemo(
        () =>
            clientsForVerification.filter(
                (client) => !isIdVerificationClientDisabled(client.contactDetailId, latestStatuses),
            ),
        [clientsForVerification, latestStatuses],
    );

    const handleSelectAllClients = () => {
        const newSelected =
            effectiveSelectedClients.length === selectableClients.length
                ? []
                : selectableClients.map((client) => client.id);
        setSelectedClients(newSelected);
    };

    const handleSelectClient = (clientId: string) => {
        setSelectedClients((prevSelected) => {
            const newSelected = prevSelected.includes(clientId)
                ? prevSelected.filter((id) => id !== clientId)
                : [...prevSelected, clientId];
            return newSelected;
        });
    };

    const validateSelectedClients = () => {
        const newErrors: Record<string, { email?: string; phone?: string }> = {};
        const emailCounts = new Map<string, number>();
        const phoneCounts = new Map<string, number>();
        let hasError = false;

        const filteredClients = clients.filter((client) =>
            effectiveSelectedClients.includes(client.id),
        );

        filteredClients.forEach((client) => {
            const email = client.email.trim();
            const phone = client.phone.trim();

            if (email !== '') {
                emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
            }
            if (phone !== '') {
                phoneCounts.set(phone, (phoneCounts.get(phone) || 0) + 1);
            }
        });

        filteredClients.forEach((client) => {
            const email = client.email.trim();
            const phone = client.phone.trim();

            const emailError = email === '' ? 'Please enter email' : '';
            const phoneError = getPhoneValidationError(phone, client.countryCode);
            if (emailError || phoneError) {
                newErrors[client.id] = {};
                if (emailError) newErrors[client.id].email = emailError;
                if (phoneError) newErrors[client.id].phone = phoneError;
                hasError = true;
            }
        });

        setErrors(newErrors);
        return hasError;
    };

    const handleClick = async () => {
        if (validateSelectedClients()) return;

        const profiles = clients
            .filter((client) => effectiveSelectedClients.includes(client.id))
            .map((client) => {
                const contactDetail = contactDetails?.details?.find(
                    (contact) => contact.email === client.email,
                );
                const requesterClientRequestId =
                    client.contactDetailId ?? contactDetail?.contactDetailId;

                if (!requesterClientRequestId) {
                    throw new Error('Unable to find contact detail for ID verification request');
                }

                return {
                    requesterClientRequestId,
                    email: client.email,
                    phoneNumber: client.phone,
                    countryCode: client.countryCode || 'ca',
                    dialCode: client.dialCode || 1,
                    isSecondaryIdRequired: idVerification?.[client.id] || false,
                    isBankingVerificationRequired: bankReview?.[client.id] || false,
                };
            });

        await handleSendIDVerification({
            prospectVersionId: contactDetails?.prospectVersionId,
            prospectId: prospect?.prospectId,
            requesterReferenceId: prospect?.quoteApprover?.userId?.toString(),
            initiatorName: prospect?.quoteApprover?.fullName,
            initiatorEmail: prospect?.quoteApprover?.email,
            initiatorOrganizationName: prospect.lawFirm.companyName,
            createNewClientRecord: true,
            profiles,
        });
        await refreshVerificationRequests();
    };

    const groupedClients = clients.reduce(
        (acc, client) => {
            if (!acc[client.category]) {
                acc[client.category] = [];
            }
            acc[client.category].push(client);
            return acc;
        },
        {} as Record<string, typeof clients>,
    );

    const refreshVerificationRequests = async () => {
        await queryClient.invalidateQueries({
            queryKey: ['prospect-verification-requests', prospectId],
        });
    };

    return (
        <Box sx={modalStyles}>
            <Box sx={modalBodyStyles}>
                <Typography variant="h2">ID Verification</Typography>
                <Typography variant="body2" sx={{ marginTop: '8px' }}>
                    Please verify the information below to proceed with ID verification.
                </Typography>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={
                                    selectableClients.length > 0 &&
                                    effectiveSelectedClients.length === selectableClients.length
                                }
                                onChange={handleSelectAllClients}
                            />
                        }
                        label="Select All"
                    />
                    {Object.entries(groupedClients).map(([category, categoryClients]) => (
                        <div key={category}>
                            {category !== 'Other' && (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        marginBottom: 2,
                                        fontWeight: 'bold',
                                        color: theme.palette.primary.main,
                                    }}
                                >
                                    {category}
                                </Typography>
                            )}

                            {categoryClients?.map((client) => {
                                const isDisabled = isIdVerificationClientDisabled(
                                    client.contactDetailId,
                                    latestStatuses,
                                );

                                return (
                                    <Box
                                        key={client.id}
                                        sx={{
                                            marginBottom: 2,
                                            padding: 2,
                                            border: '1px solid #ddd',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={effectiveSelectedClients.includes(
                                                                client.id,
                                                            )}
                                                            onChange={() =>
                                                                handleSelectClient(client.id)
                                                            }
                                                            disabled={isDisabled}
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                color: isDisabled
                                                                    ? 'text.disabled'
                                                                    : 'inherit',
                                                            }}
                                                        >
                                                            {client.name}
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Grid container spacing={3} alignItems="center">
                                                    <Grid item xs={7}>
                                                        <Box sx={{ mb: 2 }}>
                                                            <TextField
                                                                variant="outlined"
                                                                label="Email"
                                                                value={client.email}
                                                                onChange={(e) =>
                                                                    handleClientChange(
                                                                        client.id,
                                                                        'email',
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                fullWidth
                                                                size="small"
                                                                error={
                                                                    errors[client.id]?.email ||
                                                                    false
                                                                }
                                                                helperText={
                                                                    errors[client.id]?.email
                                                                        ? 'Please enter email'
                                                                        : ''
                                                                }
                                                                disabled={isDisabled || true}
                                                            />
                                                        </Box>
                                                        <CustomPhoneInput
                                                            value={client.phone}
                                                            selectedCountryCode={
                                                                client.countryCode || 'ca'
                                                            }
                                                            onChange={(value) =>
                                                                handlePhoneChange(client.id, value)
                                                            }
                                                            onCountryChange={(country) =>
                                                                handleCountryChange(
                                                                    client.id,
                                                                    country,
                                                                )
                                                            }
                                                            disabled={isDisabled}
                                                            error={!!errors[client.id]?.phone}
                                                            helperText={errors[client.id]?.phone}
                                                        />
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        xs={5}
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'space-between',
                                                            gap: '16px',
                                                            height: '100%',
                                                        }}
                                                    >
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        idVerification[client.id] ||
                                                                        false
                                                                    }
                                                                    onChange={() =>
                                                                        setIdVerification(
                                                                            (prev) => ({
                                                                                ...prev,
                                                                                [client.id]:
                                                                                    !prev[
                                                                                        client.id
                                                                                    ],
                                                                            }),
                                                                        )
                                                                    }
                                                                    disabled={isDisabled}
                                                                />
                                                            }
                                                            label="Request Second ID"
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        bankReview[client.id] ||
                                                                        false
                                                                    }
                                                                    onChange={() =>
                                                                        setBankReview((prev) => ({
                                                                            ...prev,
                                                                            [client.id]:
                                                                                !prev[client.id],
                                                                        }))
                                                                    }
                                                                    disabled={isDisabled}
                                                                />
                                                            }
                                                            label="Include Banking Verification"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                );
                            })}
                        </div>
                    ))}
                </FormGroup>
            </Box>
            <Divider />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: '16px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                    }}
                >
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Tooltip
                        title={
                            effectiveSelectedClients.length === 0 ? 'Select a user to submit' : ''
                        }
                        disableHoverListener={effectiveSelectedClients.length > 0}
                        arrow
                        placement="top"
                    >
                        <span>
                            <LoadingButton
                                variant="contained"
                                loading={isLoadingButton}
                                onClick={handleClick}
                                disabled={effectiveSelectedClients.length === 0}
                            >
                                Confirm
                            </LoadingButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
}
